import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { FaArrowLeft, FaEye, FaCheckCircle, FaExclamationTriangle } from 'react-icons/fa';
import BlogForm from '../../../../components/admin/forms/BlogForm';
import { apiFetch } from '../../../../utils/api';

export default function EditBlogPage() {
  const { id } = useParams();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notification, setNotification] = useState(null);

  useEffect(() => {
    let isMounted = true;

    const fetchBlogForEdit = async () => {
      try {
        setLoading(true);
        setError(null);
        const res = await apiFetch(`/api/blogs/${id}`);

        if (!res.ok) {
          if (res.status === 404) {
            setError('Blog post not found.');
          } else {
            const errorData = await res.json().catch(() => ({ message: 'Server error. Please try again.' }));
            setError(errorData.message);
          }
          if (isMounted) setBlog(null);
          return;
        }

        const blogToEdit = await res.json();
        if (isMounted) {
          setBlog(blogToEdit);
        }
      } catch (err) {
        console.error(err);
        if (isMounted) setError('An unexpected error occurred. Please try again.');
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchBlogForEdit();

    return () => {
      isMounted = false;
    };
  }, [id]);

  const handleUpdateBlog = async (blogData) => {
    try {
      setNotification(null);
      const res = await apiFetch(`/api/blogs/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(blogData),
      });

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to update blog');
      }

      setNotification({ type: 'success', message: 'Blog updated successfully!' });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      console.error(err);
      setNotification({ type: 'error', message: err.message || 'Failed to update blog post.' });
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-[400px] items-center justify-center rounded-2xl border border-slate-200 bg-white p-12">
        <div className="flex flex-col items-center gap-3">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-amber-400 border-t-transparent" />
          <p className="text-sm font-medium text-slate-500">Loading blog details...</p>
        </div>
      </div>
    );
  }

  if (error || !blog) {
    return (
      <div className="rounded-2xl border border-red-200 bg-white p-8 text-center shadow-sm">
        <p className="text-base font-semibold text-red-600">{error || 'Blog not found.'}</p>
        <Link
          to="/admin/blogs"
          className="mt-4 inline-flex items-center gap-2 rounded-xl bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800"
        >
          <FaArrowLeft /> Return to Blogs
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Top Navigation Bar */}
      <div className="flex flex-col gap-3 rounded-xl border border-slate-200 bg-white p-4 sm:p-5 shadow-2xs sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="flex items-center gap-1.5 text-[11px] font-medium text-slate-500">
            <Link to="/admin/blogs" className="hover:text-slate-800">Blogs</Link>
            <span>/</span>
            <span className="text-amber-600 font-semibold">Edit Article</span>
          </div>
          <h1 className="mt-0.5 text-lg font-bold text-slate-900 sm:text-xl">Edit Blog Post</h1>
          <p className="text-xs text-slate-500 line-clamp-1">Editing &ldquo;{blog.title}&rdquo;</p>
        </div>

        <div className="flex items-center gap-2">
          {blog.slug && (
            <Link
              to={`/admin/blogs/preview/${blog.slug}`}
              className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 shadow-2xs transition hover:bg-slate-50"
            >
              <FaEye className="text-[10px]" /> View Live
            </Link>
          )}
          <Link
            to="/admin/blogs"
            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 shadow-2xs transition hover:bg-slate-50"
          >
            <FaArrowLeft className="text-[10px]" /> Back to Blogs
          </Link>
        </div>
      </div>

      {/* Notification Toast */}
      {notification && (
        <div
          className={`flex items-center gap-3 rounded-2xl p-4 text-sm font-medium shadow-sm transition ${
            notification.type === 'success'
              ? 'border border-emerald-200 bg-emerald-50 text-emerald-800'
              : 'border border-red-200 bg-red-50 text-red-800'
          }`}
        >
          {notification.type === 'success' ? <FaCheckCircle className="text-emerald-600" /> : <FaExclamationTriangle className="text-red-600" />}
          <span>{notification.message}</span>
        </div>
      )}

      {/* Main Blog Form */}
      <BlogForm onSubmit={handleUpdateBlog} initialData={blog} />
    </div>
  );
}
